import React from 'react';

import { formatMoney } from '@betterment/js-runtime';
import { Banner, Box, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';

import { NarrowTypename } from '../../../../../graphql/utils/narrow-typename';
import { titleCss } from '../../../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../../../Shared/urlBuilder';
import { PurposeAccountsCardQuery } from '../../graphql/purposeAccountsCard.generated';

type EnvelopeAccount = PurposeAccountsCardQuery['purpose']['envelope']['accounts'][number];
type Five29Account = NarrowTypename<EnvelopeAccount, 'Five29Account'>;
type Props = { account: Five29Account };

export const Five29AccountRow: React.FC<Props> = ({ account }) => {
  const { name, legacySubAccountId: subAccountId, legacyGoalId: goalId, isSetupComplete } = account;
  const isFrozen = account.accountConnection?.isFrozen;
  const hasPendingSsoConnection = account.accountConnection?.hasPendingSsoConnection;
  const isSetupIncomplete = !isSetupComplete;
  const balance = account.accountConnection?.balance;
  const formattedBalance = balance ? formatMoney(balance) : null;
  const trailingText = isSetupIncomplete || hasPendingSsoConnection ? 'Finish setup' : formattedBalance;
  const url = (() => {
    if (isSetupIncomplete) return urlBuilder.appFiveTwoNineCompleteGoalSetup.absolute({ goalId });
    if (isFrozen || hasPendingSsoConnection) return urlBuilder.appAscensusSsoInitiations.absolute({ subAccountId });
    return urlBuilder.appExternalAccounts.absolute();
  })();
  return (
    <>
      <Box className="group">
        <SambaAutoLink to={url} css={{ textDecoration: 'none!important' }} aria-labelledby={name}>
          <ListRow css={{ _groupHover: { cursor: 'pointer' } }}>
            <ListRow.LeadingAsset>
              <Box css={{ width: '36px', height: '36px' }}>
                <ThemedImage themedAsset={account.icon} alt={`${name} icon`} />
              </Box>
            </ListRow.LeadingAsset>
            <ListRow.LeadingContent>
              <Text color="text" css={titleCss}>
                {name}
              </Text>
            </ListRow.LeadingContent>
            <ListRow.TrailingContent css={{ alignItems: 'end' }}>
              {trailingText && (
                <ListRow.TrailingContent>
                  <Text color="text">{trailingText}</Text>
                </ListRow.TrailingContent>
              )}
            </ListRow.TrailingContent>
            <ListRow.TrailingAsset>
              <Icon color="icon" name="CaretRight" size="small" />
            </ListRow.TrailingAsset>
            {(isFrozen || hasPendingSsoConnection) && (
              <ListRow.BottomContent css={{ marginTop: 'space.300', gridColumnStart: '2', gridColumnEnd: '4' }}>
                <Banner variant="warning">
                  <Banner.Content>
                    {isFrozen && (
                      <Text variant="text100" color="text">
                        We can’t accept deposits right now due to a 529 account issue. Log in to the plan website to
                        learn more.
                      </Text>
                    )}
                    {hasPendingSsoConnection && (
                      <Text variant="text100" color="text">
                        You need to link your account. Connecting your account with the plan website lets you have more
                        full management of your 529.
                      </Text>
                    )}
                  </Banner.Content>
                </Banner>
              </ListRow.BottomContent>
            )}
          </ListRow>
        </SambaAutoLink>
      </Box>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
    </>
  );
};
